import { z } from 'zod4';

import { isoDatetimeToDate } from './dates';

export const SpotlightStatus = z.enum(['pending', 'processing', 'completed', 'failed']);

export const SpotlightUser = z.object({
	id: z.string(),
	displayName: z.string(),
	displayFirstName: z.string().nullable().optional(),
	username: z.string().optional(),
	profilePicUrl: z.string().nullable().optional(),
});

// Supporting evidence can be a string (legacy) or an object with bonus_id/source and excerpt
export const SupportingEvidenceItem = z.union([
	z.string(),
	z.object({
		bonusId: z.string().optional(),
		source: z.string().optional(),
		excerpt: z.string(),
	}),
]);

export type SupportingEvidenceItem = z.infer<typeof SupportingEvidenceItem>;

export const EmployeeSpotlightSection = z.object({
	id: z.string(),
	intro: z.string(),
	title: z.string(),
	textHtml: z.string(),
	supportingEvidence: z.array(SupportingEvidenceItem).optional(),
	type: z.string(), // Used for display label, not strictly validated
	strategy: z.string().optional(),
	scores: z.record(z.string(), z.unknown()).optional(),
});

export type EmployeeSpotlightSection = z.infer<typeof EmployeeSpotlightSection>;

export const SpotlightVisibility = z.enum(['private', 'company']);

export const EmployeeSpotlight = z.object({
	id: z.string(),
	userId: z.string(),
	companyId: z.string(),
	generatedByUserId: z.string().nullable(),
	visibility: SpotlightVisibility.default('private'),
	bonusIds: z.array(z.string()).default([]),
	user: SpotlightUser.nullable().optional(),
	startTime: isoDatetimeToDate,
	endTime: isoDatetimeToDate,
	status: SpotlightStatus,
	totalReceivedCount: z.number().default(0),
	totalGivenCount: z.number().default(0),
	monthsAtCompany: z.number().default(0),
	generationStartedAt: isoDatetimeToDate.nullable(),
	generationCompletedAt: isoDatetimeToDate.nullable(),
	generationError: z.string().nullable(),
	sections: z.array(EmployeeSpotlightSection).default([]),
	data: z.record(z.string(), z.unknown()).optional(),
	createdAt: isoDatetimeToDate,
	updatedAt: isoDatetimeToDate,
	// Image and URL fields for spotlight attachment
	url: z.string(),
	imageUrl: z
		.string()
		.nullable()
		.optional()
		.transform((v) => v ?? undefined),
	imageHeight: z
		.number()
		.int()
		.nullable()
		.optional()
		.transform((v) => v ?? undefined),
	imageWidth: z
		.number()
		.int()
		.nullable()
		.optional()
		.transform((v) => v ?? undefined),
	thumbnailUrl: z
		.string()
		.nullable()
		.optional()
		.transform((v) => v ?? undefined),
	thumbnailHeight: z
		.number()
		.int()
		.nullable()
		.optional()
		.transform((v) => v ?? undefined),
	thumbnailWidth: z
		.number()
		.int()
		.nullable()
		.optional()
		.transform((v) => v ?? undefined),
});

export type EmployeeSpotlight = z.infer<typeof EmployeeSpotlight>;
